import { config } from '../config';
import {
  TERMINAL_STATUSES,
  type PaymentOption,
  type PaymentRecord,
  type PublicSession,
  type WirePaymentOption,
  type WireSession,
} from './types';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

/* ------------------------------------------------------------------------ */
/* Normalisation: absorb aliases so the rest of the page sees one shape     */
/* ------------------------------------------------------------------------ */

function symbolFromAsset(asset: string): string {
  return (asset.split(':')[0] || asset).toUpperCase();
}

function normalizeOption(o: WirePaymentOption): PaymentOption {
  const networkId = o.network_id ?? (o.chain_id == null ? null : String(o.chain_id));
  const chainIdNum = networkId != null && networkId !== '' ? Number(networkId) : NaN;
  const contract = o.contract_address ?? o.token_contract ?? null;
  const uri = o.payment_uri ?? o.uri ?? '';
  return {
    ...o,
    symbol: o.symbol || symbolFromAsset(o.asset),
    network_id: networkId,
    chain_id: Number.isFinite(chainIdNum) ? chainIdNum : undefined,
    contract_address: contract,
    token_contract: contract ?? undefined,
    payment_uri: uri,
    uri,
    qr_payload: o.qr_payload || uri || o.address,
  };
}

function normalizePayment(p: NonNullable<WireSession['payments']>[number]): PaymentRecord {
  return {
    asset: p.asset ?? '',
    symbol: p.symbol || (p.asset ? symbolFromAsset(p.asset) : ''),
    amount: p.amount ?? '0',
    amount_minor: p.amount_minor ?? '0',
    confirmations: p.confirmations ?? 0,
    required_confirmations: p.required_confirmations ?? 1,
    ...p,
  } as PaymentRecord;
}

export function normalizeSession(w: WireSession): PublicSession {
  return {
    ...w,
    purpose: w.purpose === 'credits' ? 'credits' : 'payment',
    payment_options: (w.payment_options ?? []).map(normalizeOption),
    payments: (w.payments ?? []).map(normalizePayment),
    paid: w.paid ?? null,
    success_url: w.success_url ?? null,
    cancel_url: w.cancel_url ?? null,
  };
}

/* ------------------------------------------------------------------------ */
/* Fetch + watch                                                             */
/* ------------------------------------------------------------------------ */

export async function fetchSession(id: string, signal?: AbortSignal): Promise<PublicSession> {
  const res = await fetch(`${config.apiBase}/checkout-sessions/${encodeURIComponent(id)}/public`, {
    headers: { accept: 'application/json' },
    signal,
    credentials: 'omit',
    cache: 'no-store',
  });
  if (!res.ok) throw new ApiError(res.status, `Session fetch failed (${res.status})`);
  return normalizeSession((await res.json()) as WireSession);
}

export interface SessionWatcher {
  stop(): void;
}

/**
 * Watches a session.
 *
 *  - Opens SSE (`event: status` carries the session, `event: heartbeat` every 15 s,
 *    `id:` frames so the browser resumes with Last-Event-ID).
 *  - Polls /public every 5 s as a fallback (30 s while SSE is healthy), backing off to 60 s on errors.
 *  - The server closes the stream ~2 s after a terminal status. That close is expected:
 *    once we have seen a terminal status we close our side, stop polling and never report an error.
 *    A close *before* terminal is a failure: we drop to fast polling (EventSource keeps retrying).
 */
export function watchSession(
  id: string,
  onUpdate: (s: PublicSession) => void,
  onError: (e: unknown) => void,
): SessionWatcher {
  let stopped = false;
  let terminal = false;
  let es: EventSource | null = null;
  let sseHealthy = false;
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let interval = config.pollIntervalMs;

  const finish = () => {
    terminal = true;
    es?.close();
    es = null;
    if (pollTimer) clearTimeout(pollTimer);
    pollTimer = null;
  };

  const deliver = (s: PublicSession) => {
    if (stopped) return;
    onUpdate(s);
    if (TERMINAL_STATUSES.has(s.status)) finish();
  };

  const poll = async () => {
    if (stopped || terminal) return;
    try {
      deliver(await fetchSession(id));
      interval = config.pollIntervalMs;
    } catch (e) {
      onError(e);
      interval = Math.min(interval * 2, 60_000);
    } finally {
      if (!stopped && !terminal) pollTimer = setTimeout(poll, sseHealthy ? Math.max(interval, 30_000) : interval);
    }
  };

  const openSse = () => {
    if (typeof EventSource === 'undefined') return;
    try {
      es = new EventSource(`${config.apiBase}/checkout-sessions/${encodeURIComponent(id)}/events`);
      const onStatus = (ev: Event) => {
        sseHealthy = true;
        try {
          const data = JSON.parse((ev as MessageEvent).data) as WireSession | { session?: WireSession };
          const wire = 'payment_options' in data ? (data as WireSession) : (data as { session?: WireSession }).session;
          if (wire?.id) deliver(normalizeSession(wire));
        } catch (e) {
          onError(e);
        }
      };
      es.addEventListener('status', onStatus);
      es.addEventListener('message', onStatus); // servers that omit `event:`
      es.addEventListener('heartbeat', () => {
        sseHealthy = true;
      });
      es.onopen = () => {
        sseHealthy = true;
      };
      es.onerror = () => {
        if (terminal || stopped) {
          es?.close();
          es = null;
          return;
        }
        // Pre-terminal failure: EventSource will retry on its own; meanwhile poll at full speed.
        sseHealthy = false;
        if (es && es.readyState === EventSource.CLOSED) es = null; // e.g. 404: endpoint absent
      };
    } catch {
      es = null;
    }
  };

  openSse();
  void poll();

  return {
    stop() {
      stopped = true;
      finish();
    },
  };
}
